import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { i18nIntl } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';
import { stage_promotion_types } from '../../data/bootstrap_stages';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import BuilderCanvas from '../builder/BuilderCanvas';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import FloatingLayer from '../builder/FloatingLayer';
import { AutoPostImageUploader } from '../builder/ImageUploader';
import Input from '../builder/Input';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import RichEditor from '../builder/RichEditor.js';
import SectionSubtitle from '../builder/SectionSubtitle';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';
import TextArea from '../builder/TextArea';

import Layout from '../front/Layout';
import PhotoFunStage from '../front/PhotoFunStage';

import photo_editor_data from '../../data/photo_editor_packs';
import Tooltip from '../builder/Tooltip.js';
import { DeleteButton } from '../builder/ConfirmButton.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import MediaUploader from '../builder/MediaUploader.js';
import DisabledArea from '../builder/DisabledArea.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));
const InputPlaceHolder = injectIntl(transferPlaceholder(Input));

const DragHandle = SortableHandle(() => <div class="sorter_target" />);

const sizes = {
	square:{
		id:'1x1',
		width:800,
		height:800,
		sticker:{ width:450, height:450 },
	},
	post:{
		id:'120x63',
		width:1200,
		height:630,
		sticker:{ width:450, height:450 },
	},
	story:{
		id:'375x667',
		width:1127,
		height:2008,
		sticker:{ width:450, height:450 },
	},
};

const SortableFields = SortableContainer(({ items, type, img_proportion, deleteImage, editname, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_scenes = props?.is_scenes ?? false;

	return (
		<div class="group_tabled_items gui_not_in_top_big">
			<ul class="gui_sortable_table">
				<li class="gui_sortable_table_header">
					<div style={{ flexBasis:'calc(1em)', flexShrink:'0' }} />

					<div class="content" style={{ flexBasis:'calc(3rem - 3px + 2em)', flexShrink:'0' }}>{i18n("Image")}</div>

					<div style={{ flexGrow:'1', minWidth:'150px' }}>{i18n("***Name")}</div>

					{(is_scenes === false) && (
						<div style={{ flexBasis:'15rem' }}>{i18n("Theme")}</div>
					)}

					<div class="gui_text_align_end" style={{ flexBasis:'220px', flexShrink:'0' }}>{i18n("Actions")}</div>
				</li>
			</ul>

			<ul class="gui_sortable_table">
				{items.map((value, index) => (
					<SortableItem
						key={`item-${index}`}
						index={index}
						value={value}
						type={type}
						is_scenes={is_scenes}
						deleteImage={deleteImage}
						editname={editname}
					/>
				))}
			</ul>
		</div>
	);
});

const SortableItem = SortableElement(({ value, type, deleteImage, editname, is_scenes = false }) => {
	const [ preview, setPreview ] = useState(false);

	return (
		<li class="gui_sortable_table_item">
			<DragHandle />

			<div class="content" style={{ flexBasis:'3rem', flexShrink:'0' }}>
				<div style={{ backgroundImage:`url(${value.full})`, backgroundSize:'contain', backgroundPosition:'center center', backgroundRepeat:'no-repeat', height:'3rem', width:'3rem', aspectRatio:'1 / 1' }}>
					<img src={`${window.cdn_url}img/blank_1x1.png`} height="100%" alt={value.name} />
				</div>
			</div>

			<div style={{ flexGrow:'1', minWidth:'150px' }}>
				<strong class="gui_pos_rel gui_flexbox" style={{ height:'1.5em' }}>
					<ActionLink
						_class={{ other:'gui_small_icon gui_cutTextOnOverflow' }}
						onClick={() => editname(type, value.image)}
					>{value.name}</ActionLink>
				</strong>
			</div>

			{(is_scenes === false) && (
				<div class="content" style={{ flexBasis:'15rem' }}>
					<strong>{value.pack.toUpperCase()}</strong>
				</div>
			)}

			<ButtonCollection _class={{ other:'gui_flex_end' }} style={{ flexBasis:'220px', flexShrink:'0' }}>
				<SquaredButton
					icon='knk-magnify-in'
					onClick={() => setPreview()}
					aria-label="View"
				/>

				<SquaredButton
					icon='knk-edit'
					onClick={() => editname(type, value.image)}
					aria-label="Edit name"
				/>

				<DeleteButton
					squared={true}
					icon='knk-delete'
					onClick={() => deleteImage(value, type)}
					aria-label="Delete image"
				/>
			</ButtonCollection>

			{(preview !== false) && (
				<FloatingLayer
					_class={{ size:'small' }}
					backButton={() => setPreview(false)}
				>
					<div class="without_actions">
						<img src={value.full} style={{ maxWidth:'100%' }} alt="" />
					</div>
				</FloatingLayer>
			)}
		</li>
	)
});

const PackList = ({ packs_in_use, type, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const packs = photo_editor_data.packs;
	const stage = props.stage;

	const addPack = (pack) => {
		pack = packs.find((p) => p.pack == pack);
		
		if(stage?.photo_editor_images == undefined) {
			stage.photo_editor_images = {
				stickers:[],
				marks:[],
			};
		}

		if(type == 'stickers') {
			stage.photo_editor_images.stickers = stage.photo_editor_images.stickers.filter((sticker) => (sticker.pack != pack.pack));

			if(packs_in_use[pack.pack] == undefined) {
				stage.photo_editor_images.stickers = [...stage.photo_editor_images.stickers,...pack.stickers];
			}

			props.change('photo_editor_images', stage.photo_editor_images);
		}

		if(type == 'marks') {
			stage.photo_editor_images.marks = stage.photo_editor_images.marks.filter((mark) => (mark.pack != pack.pack));

			if(packs_in_use[pack.pack] == undefined) {
				const marks_size = pack.marks.map((mark) => {
					let size = 'square';
					switch(stage?.photo_editor_proportion) {
						case '120x63':
							size = 'post';
							break;

						case '375x667':
							size = 'story';
							break;
					}

					if(pack?.sizes) {
						return {
							...mark,
							full:mark.sizes[size],
						};
					}

					const new_mark = { ...mark };
					delete new_mark.sizes

					return new_mark;
				});

				stage.photo_editor_images.marks = [ ...stage.photo_editor_images.marks, ...marks_size ];
			}

			props.change('photo_editor_images',stage.photo_editor_images);
		}
	};

	return (
		<ul class="pack_selector">
			{packs.map((pack, i) => {
				if(pack[type].length == 0) return null;
				const status = (packs_in_use[pack.pack] != undefined && packs_in_use[pack.pack]) ? true : false;

				const total_label = (type == 'stickers') ? "{count} stickers" : "{count} frames";
				const items_in_pack = pack[(type == 'stickers') ? 'stickers' : 'marks'];

				return (
					<li key={`pack_${i}`} class={(status) ? 'selected' : null} onClick={() => addPack(pack.pack)}>
						<div class={`pack_status ${(status) ? 'knk-on whiteIcon' : 'knk-plus'}`} />

						{(type == 'stickers') && (
							<ul class="pack_preview">
								{items_in_pack.slice(0, 4).map((item, s) => {
									return (
										<li
											key={s}
											style={{ backgroundImage:`url(${item.full})` }}
										/>
									)
								})}
							</ul>
						)}

						<div class="pack_info">
							<strong>{i18n(pack.name)}</strong>

							<span class="pack_counters">
								{(items_in_pack.length > 0) && (
									<FormattedMessage
										id={total_label}
										values={{ count:items_in_pack.length }}
									/>
								)}
							</span>
						</div>
					</li>
				)
			})}
		</ul>
	)
};

const ImagesMessage = ({ type, size_helper, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const display = props?.display ?? 'inline';
	let title_class;
	switch(display) {
		case 'inline':
		default:
			title_class = {
				other:'gui_not_in_bottom_small in_top',
			};
			break;

		case 'layer':
			title_class = {
				only_text:true,
				other:'gui_not_in_bottom_small',
			};
			break;
	}

	let section_title;
	let section_subtitle;
	let recommendations;

	switch(type) {
		case 'marks':
			section_title = "Add custom frames";
			section_subtitle = "Select one or more images to add your own creations of frames to the current theme.";
			recommendations = [
				i18n("PNG with transparency format."),
				i18n("You can upload multiple frames by choosing a folder."),
				i18n("The file name will be displayed as the frame's public name."),
				`${i18n("Image size must be")}: ${size_helper.width} × ${size_helper.height} px.`,
			];
			break;

		case 'stickers':
			section_title = "Add custom stickers";
			section_subtitle = "Select one or more images to add your own creations of stickers to the current theme.";
			recommendations = [
				i18n("PNG with transparency format."),
				i18n("You can upload multiple stickers by choosing a folder."),
				i18n("The file name will be displayed as the sticker's public name."),
				`${i18n("Recommended image size")}: ${size_helper.sticker.width} × ${size_helper.sticker.height} px.`,
			];
			break;

		case 'scenes':
			section_title = "Add custom scenes";
			section_subtitle = "Select one or more images to add your own creations of scenes to the current theme.";
			recommendations = [
				i18n("JPG format."),
				i18n("You can upload multiple scenes by choosing a folder."),
				i18n("The file name will be displayed as the scene's public name."),
				`${i18n("Image size must be")}: ${size_helper.width} × ${size_helper.height} px.`,
			];
			break;
	}

	return (
		<div>
			<div>
				<SectionTitle _class={title_class}>{section_title}</SectionTitle>

				<SectionSubtitle>{section_subtitle}</SectionSubtitle>

				<div class="recommendations_box gui_not_in_top_small">
					<strong>{i18n("Recommendations")}:</strong>

					<ul class="bulleted_list">
						{recommendations.map((r, i) => {
							return (
								<li key={i}>{r}</li>
							)
						})}
					</ul>
				</div>
			</div>

			<UploadCustomItemsLayer
				type={type}
				stage={props.stage}
				change={props.change}
				afterUpload={props.afterUpload}
				promotion_id={props.promotion_id}
			/>

			<p class="is_legend label_additional_msg gui_not_in_top_small gui_in_start whole_contents">
				<FormattedMessage
					id="Check out other recommendations and examples on {link}."
					values={{
						link:<ActionLink
							_href={`${window.zendesk_base_url}360020944332`}
							_target="_blank"
						>how to add your own creations</ActionLink>,
					}}
				/>
			</p>
		</div>
	)
};

const UploadCustomItemsLayer = ({ type, ...props }) => {
	const auto_type = type == 'scenes' ? 'marks' : type;
	const uploadImage = (k, v, name) => {

		const target_array = props.stage.photo_editor_images[auto_type];
		const parts = name.split(".");
		const no_ext = parts.slice(0, -1).join(".");

		if(k == 'image') {
			const new_array = [...target_array, {
				name:no_ext,
				pack:'custom',
				image:v,
			}];

			props.change('photo_editor_images', {...props.stage.photo_editor_images, [auto_type]:new_array });
		}

		if(k == 'full_image') {
			const new_array = target_array.map((image) => {
				if(v.indexOf(image.image) < 0 ) return image;

				return {
					...image,
					full:v
				};
			});

			props.change('photo_editor_images', {...props.stage.photo_editor_images, [auto_type]:new_array });
		}
		if(props?.afterUpload) props.afterUpload()
	};

	

	return (
		<AutoPostImageUploader
			name="image"
			nopreview={true}
			auto_url={`/promotions/auto_img/${props.promotion_id}`}
			auto_type={auto_type}
			change={uploadImage}
			_thumb_size={{ width:'100%', height:150 }}
		/>
	)
};

const PhotoFunStickers = ({ type, size_helper, deleteImage, editname, setUploadNewItems, stage, change, onSortEnd, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stickers = stage?.photo_editor_images?.stickers ?? [];

	const getPacksInUse = () => {
		const packs_in_use = {};

		for(let i in stickers) {
			packs_in_use[stickers[i].pack] = 1;
		}

		return packs_in_use;
	};

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<BuilderSubSectionTitle
						title="Select your themes"
						icon='knk-list'
						_class='gui_not_in_bottom_tiny'
					/>

					<PackList
						packs_in_use={getPacksInUse()}
						change={change}
						stage={stage}
						type="stickers"
						intl={props.intl}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				not_bordered={true}
				not_awesome={true}
				extra_classes='main_bg'
				content_classes='gui_is_padded'
			>
				{(stickers.length == 0) && (
					<div style={{minHeight:'135px', paddingBlockEnd:'10px'}}>
						<ImagesMessage
							type="stickers"
							stage={stage}
							change={change}
							promotion_id={stage.promotion_id}
							size_helper={size_helper}
							intl={props.intl}
						/>
					</div>
				)}

				{(stickers.length > 0) && (
					<article>
						<BuilderSubSectionTitle
							title={`${i18n("Stickers")} (${stickers.length})`}
							icon='knk-sticker'
							title_actions={<ActionLink
								onClick={() => setUploadNewItems('stickers')}
							>Upload your own stickers</ActionLink>}
						/>

						<SortableFields
							items={stickers}
							type="stickers"
							editname={editname}
							img_proportion="1x1"
							deleteImage={deleteImage}
							onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, 'stickers')}
							axis="xy"
							useDragHandle={true}
							intl={props.intl}
						/>
					</article>
				)}
			</BuilderCanvas>
		</>
	)
};

const PhotoFunFrames = ({ type, size_helper, deleteImage, editname, setUploadNewItems, stage, change, onSortEnd, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const subtype = (type == 'frame') ? 'marks' : 'marks';

	const marks = stage?.photo_editor_images?.marks ?? [];
	const img_proportion = stage?.photo_editor_proportion ?? '1x1';	// w:h

	const getPacksInUse = () => {
		const packs_in_use = {};

		for(let i in marks) {
			packs_in_use[marks[i].pack] = 1;
		}

		return packs_in_use;
	};

	const is_scenes = (parseInt(stage.promotion_type_id) == stage_promotion_types.SCENES.id) ? true : false;
	if(is_scenes) {
		return (
			<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				{(marks.length == 0) && (
					<div style={{ minHeight:'135px', paddingBlockEnd:'10px' }}>
						<ImagesMessage
							type="scenes"
							stage={stage}
							change={change}
							promotion_id={stage.promotion_id}
							size_helper={size_helper}
							intl={props.intl}
						/>
					</div>
				)}

				{(marks.length > 0) && (
					<article>
						<BuilderSubSectionTitle
							title={`${i18n("Scenes")} (${marks.length})`}
							icon='knk-frame'
							title_actions={<ActionLink
								onClick={() => setUploadNewItems(subtype)}
							>Upload a new scene</ActionLink>}
						/>

						<SortableFields
							items={marks}
							type="marks"
							editname={editname}
							is_scenes={true}
							img_proportion={img_proportion}
							deleteImage={deleteImage}
							onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, subtype)}
							axis="xy"
							useDragHandle={true}
							intl={props.intl}
						/>
					</article>
				)}
			</BuilderMainWithoutSidebar>
		)
	}

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<BuilderSubSectionTitle
						title="Select your themes"
						icon='knk-list'
						_class='gui_not_in_bottom_tiny'
					/>

					<PackList
						packs_in_use={getPacksInUse()}
						type="marks"
						change={change}
						stage={stage}
						intl={props.intl}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				not_bordered={true}
				not_awesome={true}
				extra_classes='main_bg'
				content_classes='gui_is_padded'
			>
				{(marks.length == 0) && (
					<div style={{ minHeight:'135px', paddingBlockEnd:'10px' }}>
						<ImagesMessage
							type="marks"
							stage={stage}
							change={change}
							promotion_id={stage.promotion_id}
							size_helper={size_helper}
							intl={props.intl}
						/>
					</div>
				)}

				{(marks.length > 0) && (
					<article>
						<BuilderSubSectionTitle
							title={`${i18n("Frames")} (${marks.length})`}
							icon='knk-frame'
							title_actions={<ActionLink
								onClick={() => setUploadNewItems(subtype)}
							>Upload your own frames</ActionLink>}
						/>

						<SortableFields
							items={marks}
							type="marks"
							editname={editname}
							img_proportion={img_proportion}
							deleteImage={deleteImage}
							onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, subtype)}
							axis="xy"
							useDragHandle={true}
							intl={props.intl}
						/>
					</article>
				)}
			</BuilderCanvas>
		</BuilderMain>
	)
};

const PhotoFunSettings = ({ type, size_helper, stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors, setErrors ] = useState({});

	const slider_min = parseInt(stage?.photo_editor_num_stickers_min ?? '0');
	const slider_max = parseInt(stage?.photo_editor_num_stickers_max ?? '10');

	const avail_img_proportions = [
		{ k:'1x1', v:`${i18n("Square")} (${sizes.square.width} × ${sizes.square.height})` },
		{ k:'120x63', v:`${i18n("Post")} (${sizes.post.width} × ${sizes.post.height})` },
		{ k:'375x667', v:`${i18n("Story")} (${sizes.story.width} × ${sizes.story.height})` },
	];

	const avail_scaling_modes = [
		{ k:'0', v:i18n("Disabled") },
		{ k:'1', v:i18n("Scale non uniformly") },
		{ k:'2', v:`${i18n("Scale uniformly")} (${i18n("default")})` },
	];

	const default_input_class = { size:60 };

	let default_description_text = `<h1>${i18n("Upload your image")}</h1><p>${i18n("You will be able to customize it later.")}</p>`;
	if(stage.promotion_type_id == stage_promotion_types.SCENES.id) default_description_text = `<h1>${i18n("Preview and submit")}</h1>`;
	const video_url = (stage.main_video) ? stage.main_video.url : null;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				{(type != 'scene') && (
					<article>
						<Model model={stage} change={change}>
							<MediaUploader
								imageField="main_image"
								videoField="main_video"
								image={stage.full_main_image}
								video={video_url}
								_class={{first:true}}
							>Upload media</MediaUploader>

							<RichEditorPlaceHolder
								placeholder={default_description_text}
								name="main_description"
								has_emojis={true}
							>Introduction message</RichEditorPlaceHolder>

							<Input
								_class={default_input_class}
								name="photo_editor_label"
								placeholder={(type != 'scene') ? "Upload and edit image" : "Click to start"}
							>Button label</Input>
						</Model>
					</article>
				)}

				<article class={(type != 'scene') ? "gui_not_in_top_huge" : null}>
					{(type != 'scene') && (
						<BuilderSubSectionTitle
							title="Settings"
							icon='knk-settings'
						/>
					)}

					<Model model={stage} change={change}>
						{/*
						// always square
						<Select
							name="photo_editor_proportion"
							_class={{ ...default_input_class, first:(type == 'scene') ? true : null }}
							values={avail_img_proportions}
						>Proportion</Select>*/}

						{(type != 'scene') && (
							<Input
								_class={{ ...default_input_class, first:(type == 'scene') ? true : null }}
								name="photo_editor_label_photo"
								placeholder="Photo"
							>Photo label</Input>
						)}

						<Input
							_class={default_input_class}
							name="photo_editor_label_stickers"
							placeholder="Stickers"
						>Stickers label</Input>

						<Input
							_class={default_input_class}
							name="photo_editor_label_frames"
							placeholder={(type != 'scene') ? "Frames" : "Scenes"}
						>{(type != 'scene') ? "Frames label" : "Scenes label"}</Input>

						<Select
							name="photo_editor_scaling_mode"
							_class={default_input_class}
							values={avail_scaling_modes}
						>Scaling mode</Select>

						<Checkbox
							name="photo_editor_sticker_name"
						>{(type != 'scene') ? "Display sticker and frame names?" : "Display sticker and scene names?"}</Checkbox>

						{(type != 'scene') && (
							<Checkbox
								name="photo_editor_mandatory_frame"
							>Is the frame mandatory?</Checkbox>
						)}

						<Range
							min={0}
							max={slider_max}
							name="photo_editor_num_stickers_min"
							step={1}
							value={slider_min}
							_class={{ size:40 }}
						>Min. number of stickers</Range>

						<Range
							min={slider_min}
							max={50}
							name="photo_editor_num_stickers_max"
							step={1}
							value={slider_max}
							_class={{ size:40 }}
						>Max. number of stickers</Range>

						{(type == 'scene') && (
							<AutoPostImageUploader
								auto_url={`/promotions/auto_img/${stage.promotion_id}`}
								auto_type="stages"
								name="photo_editor_custom"
								image={stage.full_photo_editor_custom}
								_size={size_helper}
								_thumb_size={{height:185}}
								label_props={{
									tooltip:<Tooltip>Upload an image with the participation instructions. It will be visible to users just before creating their scene.</Tooltip>,
									extra_contents:i18n("Optional"),
								}}
							>Instructions image</AutoPostImageUploader>
						)}

						{(type == 'scene') && (
							<RichEditorPlaceHolder
								placeholder={default_description_text}
								name="main_description"
								has_emojis={true}
							>Preview message</RichEditorPlaceHolder>
						)}

						<Checkbox
							name="entry_premoderation"
							_class={{ size:40 }}
						>Enable option for admins to pre-moderate the entries before they are published</Checkbox>
					</Model>
				</article>
			</BuilderSidebar>

			{(type == 'scene') && (
				<BuilderCanvas
					not_awesome={true}
					not_bordered={true}
				>
					<FormattedMessage
						id="Check out the help on the option's setup on {link}."
						values={{
							link:<ActionLink
								_href={window.zendesk_base_url + ((type != 'scene') ? '360019804612' : '360020779252')}
								_target="_blank"
							>this link</ActionLink>,
						}}
					/>
				</BuilderCanvas>
			)}

			{(type != 'scene') && (
				<BuilderCanvas>
					<Layout
						promo={props.promoModel.attributes}
						menus={props.promoModel.Custom__Menus}
						is_builder={true}
					>
						<PhotoFunStage
							builder={true}
							promotion={{
								Promotion:props.promoModel.attributes,
							}}
							stage={stage}
						/>
					</Layout>
				</BuilderCanvas>
			)}
		</>
	)
};

const PhotoFunShare = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ filters, setFilters ] = useState({ view_page:'success' });

	const defaut_text_placeholder = `<h1>${i18n("This is your design!")}</h1><p>${i18n("Share it now with your friends so they can create their own and participate in the contest too.")}</p>`;
	const main_shared_placeholder = `<h1>${i18n("The design of {name}")}</h1><p>${i18n("Want to take part in the contest? Design your own image now in less than 2 minutes.")}</p>`;

	// by default, all networks are enabled, so if promotion.config.share is not defined, we set up the networks defaults to '1' (i.e. true):
	const share_options = props?.promotion?.config?.share?.networks ?? { twitter:'1', whatsapp:'1', line:'1', telegram:'1' };

	const has_twitter_enabled = (share_options.twitter == '1') ? true : false;
	const has_mobile_enabled = (share_options.whatsapp == '1' || share_options.line == '1' || share_options.telegram == '1') ? true : false;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Model model={filters} change={(k, v) => setFilters({ ...filters, [k]:v })}>
					<Select
						name='view_page'
						values={[
							{ k:'success', v:i18n("Page after designing the image") },
							{ k:'public_share', v:i18n("Destination page of the shared link") },
						]}
						_class={{first:true}}
					>Select the page to edit</Select>
				</Model>

				{(filters.view_page == 'success') && (
					<>
						<article class="gui_not_in_top_huge">
							<Model model={stage} change={change}>
								<RichEditorPlaceHolder
									name='game_feedback_ok'
									placeholder={defaut_text_placeholder}
								>Introduction message</RichEditorPlaceHolder>

								<InputPlaceHolder
									name='button_label'
									placeholder={i18n("Share")}
								>{i18n("Label for '%s' button").replace('%s', i18n("Share"))}</InputPlaceHolder>
							</Model>
						</article>

						<article class="gui_not_in_top_huge">
							<header class="gui_section_header_builder">
								<h3 class="section_title knk-share">{i18n("Content when sharing")}</h3>
							</header>

							<Model model={stage} change={change}>
								<Input
									name="share_title"
									max_length={90}
									label_props={{
										tooltip:<Tooltip>This is the title of the publication of the viral message on Facebook. Maximum 90 characters.</Tooltip>,
									}}
								>Title</Input>

								<TextArea
									name="share_description"
									max_length={255}
									label_props={{
										tooltip:<Tooltip>This is the description of the publication of the viral message on Facebook. Maximum 255 characters.</Tooltip>,
									}}
								>Description</TextArea>
							</Model>

							<DisabledArea
								disabled={(!has_twitter_enabled) ? true : false}
								message="Twitter network must be enabled"
								link='#share'
							>
								<Model model={stage} change={change}>
									<TextArea
										name="share_tweet"
										max_length={280}
										label_props={{
											tooltip:<Tooltip>This is the text of the tweet of the viral message on Twitter. The shared link will be included automatically at the end of this text.</Tooltip>,
										}}
									>Tweet</TextArea>
								</Model>
							</DisabledArea>

							<DisabledArea
								disabled={(!has_mobile_enabled) ? true : false}
								message="Whatsapp, Telegram or Line networks must be enabled"
								link='#share'
							>
								<Model model={stage} change={change}>
									<TextArea
										name="share_instant"
										max_length={200}
										label_props={{
											tooltip:<Tooltip>This is the message that users will send through Instant Messaging Apps. The shared link will be automatically included at the end of the mensaje. This option is only available for users connnected via smartphone. Maximum length: 200 characters</Tooltip>,
										}}
									>Instant Messaging Apps (Whatsapp, Telegram, Line)</TextArea>
								</Model>
							</DisabledArea>
						</article>
					</>
				)}

				{(filters.view_page == 'public_share') && (
					<article class="gui_not_in_top_huge">
						<Model model={stage} change={change}>
							<RichEditorPlaceHolder
								name='shared_description'
								placeholder={main_shared_placeholder}
							>Introduction message</RichEditorPlaceHolder>

							<InputPlaceHolder
								name='shared_button_label'
								placeholder={i18n("Create my own")}
							>{i18n("Label for '%s' button").replace('%s', i18n("Create my own"))}</InputPlaceHolder>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
			>
				<Layout
					promo={props.promoModel.attributes}
					menus={props.promoModel.Custom__Menus}
					is_builder={true}
					layout_extra_classes="is_new_game"
				>
					<PhotoFunStage
						builder={true}
						promotion={{
							Promotion:props.promoModel.attributes,
						}}
						stage={stage}
						display_page={filters.view_page}
					/>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const updateProportion = (stage,photo_editor_data) => {
	let size = 'square';
	switch(stage?.photo_editor_proportion) {
		case '120x63':
			size = 'post';
			break;

		case '375x667':
			size = 'story';
			break;
	}
	const marks = stage.photo_editor_images.marks;
	const indexed = {};
	const new_marks = marks.map(frame => {
		if(frame.pack == 'custom') return frame;
		if(indexed?.[frame.pack] == undefined) {
			indexed[frame.pack] = photo_editor_data.packs.find((pack) => pack.pack == frame.pack);
			if(indexed[frame.pack] == undefined) return frame;
		}

		const frame_data = indexed[frame.pack].marks.find((mark) => mark.name = frame.name);

		if(frame_data?.sizes && frame_data.sizes[size]) {
			return {
				...frame,
				full:frame_data.sizes[size],
			};
		}

		return frame
	})

	return {...stage.photo_editor_images, marks:new_marks};
}

const PhotoFunBuilder = (props) => {
	useCSS('comp/photoeditor.css');

	const [ editing_image, setEditingImage ] = useState(null);
	const [ upload_new_items, setUploadNewItems ] = useState(false);

	const stage = props?.stage ?? {};
	const img_proportion = stage?.photo_editor_proportion ?? '1x1';	// w:h

	if(stage.photo_editor_images == undefined) stage.photo_editor_images = {};
	if(stage.photo_editor_images.marks == undefined) stage.photo_editor_images.marks = [];
	if(stage.photo_editor_images.stickers == undefined) stage.photo_editor_images.stickers = [];

	let type = '';
	let basename_type = '';
	switch(parseInt(stage.promotion_type_id)) {
		case stage_promotion_types.SCENES.id:
			type = 'scene';
			basename_type = 'scenes';
			break;

		default:
			type = 'frame';
			basename_type = 'photofun';
			break;
	}

	const cdn_url = '/img/photo_editor_presets/';
	// const cdn_stickers_url = `${cdn_url}stickers/`;
	// const cdn_marks_url = `${cdn_url}marks/`;

	const changeElement = (k, v) => {
		const target_array = stage.photo_editor_images[editing_image.type];

		const new_array = target_array.map(element=>{
			if(element.image != editing_image.image.image) return element;
			return {...element,[k]:v}
		})
		props.change('photo_editor_images',{...stage.photo_editor_images,[editing_image.type]:new_array})
	};

	const change = (k,v) => {
		props.change(k,v);
		if (k == 'photo_editor_proportion') {
			props.change('photo_editor_images',updateProportion(stage,photo_editor_data));
		}
	}

	const onSortEnd = (oldIndex, newIndex, type) => {
		const target_array = stage.photo_editor_images[type];
		const new_array = arrayMove(target_array,oldIndex,newIndex);
		props.change('photo_editor_images',{...stage.photo_editor_images,[type]:new_array})
	};

	const deleteImage = (image, type) => {
		const target_array = stage.photo_editor_images[type];
		const new_array = target_array.filter(item=>(item.image != image.image));
		props.change('photo_editor_images',{...stage.photo_editor_images,[type]:new_array})
		props.saveStage()
	};

	const editname = (type, image) => {
		setEditingImage({type,image:stage.photo_editor_images[type].find((f) => f.image == image)});
	};

	const basename = `/stages/${stage.id}/${basename_type}`;

	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-arrow-l-big' },
	];
	if(type != 'scene') nav_sections.push({ label:"Options", link:`${basename}/settings`, icon:'knk-settings' });
	nav_sections.push({ label:(type == 'frame') ? "Frames" : "Scenes", link:`${basename}/frames`, icon:(type == 'frame') ? 'knk-frame' : 'knk-scene' });
	nav_sections.push({ label:"Stickers", link:`${basename}/stickers`, icon:'knk-sticker' });
	nav_sections.push({ label:"Share", link:`${basename}/share`, icon:'knk-share' });
	if(type == 'scene') nav_sections.push({ label:"Options", link:`${basename}/settings`, icon:'knk-settings' });

	let size_helper = {};
	switch(img_proportion) {
		case '120x63':
			size_helper = sizes.post;
			break;

		case '375x667':
			size_helper = sizes.story;
			break;

		case '1x1':
		default:
			size_helper = sizes.square;
			break;
	}

	const generic_props = {
		...props,
		change,
		type,
		deleteImage,
		editname,
		size_helper,
		nav_sections,
		onSortEnd,
		setUploadNewItems,
	};

	return (
		<Router basename={basename} hashType="noslash">
			<Switch>
				<Route exact={true} path="/(settings|stickers|share|preview)">
					<BuilderMain pm={props.promoModel}>
						<Route exact={true} path="/settings">
							<PhotoFunSettings
								{...generic_props}
								nav_active='/settings'
							/>
						</Route>

						<Route exact={true} path="/stickers">
							<PhotoFunStickers
								{...generic_props}
								nav_active='/stickers'
							/>
						</Route>

						<Route exact={true} path="/share">
							<PhotoFunShare
								{...generic_props}
								nav_active='/share'
							/>
						</Route>
					</BuilderMain>
				</Route>

				<Route exact={true} path="/frames">
					<PhotoFunFrames
						{...generic_props}
						nav_active='/frames'
					/>
				</Route>
			</Switch>

			{(upload_new_items) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => setUploadNewItems(false)}>
					<div>
						<ImagesMessage
							type={upload_new_items}
							promotion_id={props.stage.promotion_id}
							display="layer"
							size_helper={size_helper}
							intl={props.intl}
							stage={stage}
							change={props.change}
							afterUpload={() => {
								setUploadNewItems(false)
								setTimeout(()=>{
									props.saveStage();
								},200)
							}}
						/>

						<ButtonCollection in_floating={true}>
							<SecondaryButton
								onClick={() => setUploadNewItems(false)}
							>Close</SecondaryButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}

			{(editing_image != null) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => setEditingImage(null)}>
					<div>
						<Model model={editing_image.image} change={changeElement}>
							<Input
								name="name"
								_class={{first:true, size:80}}
							>***Name</Input>
						</Model>

						<ButtonCollection in_floating={true}>
							<ActionButton
								onClick={() => setEditingImage(null)}
							>Save</ActionButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</Router>
	)
};

export default injectIntl(PhotoFunBuilder);